import React, { useState } from "react"
import { useForm } from "react-hook-form"
import GetCSFR from "../../../hooks/userAuthentication/GetCSFR"
import axios from "axios"
import { loadStripe } from "@stripe/stripe-js"

interface CustomerForm {
    email: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    province: string;
    postalCode: string;
    address: string;
    city: string;
}

interface CartItem {
    id: string;
    title: string;
    size: string;
    colour: string;
    quantity: number;
    image: string;
    price: number;
}

interface Checkout {
    order: CartItem[];
    customerDetails: CustomerForm;
}

export default function CustomerDetails(){
    const [preventSubmit, setPreventSubmit] = useState<boolean>(false)
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const {
        register,
        handleSubmit,
        formState: { errors },
      } = useForm<CustomerForm>()
    
    // Retrieve current items in cart
    function getCart() {
        const cookies = document.cookie.split("; ")
        const cartCookie = cookies.find(row => row.startsWith("cart="))
        const cartItems = cartCookie ? JSON.parse(decodeURIComponent(cartCookie.split("=")[1])) : []
        // If cartItems is a Set, convert it to an array
        if (cartItems instanceof Set) {
            return Array.from(cartItems)
        }
        return cartItems
    }

    // Helper function log axios errors
    function handleError(error: any) {
        if (error.response) {
          console.error('Error response:', error.response.data)
        } else if (error.request) {
          console.error('No response received:', error.request)
        } else {
          console.error('Error setting up request:', error.message)
        }
    }
    
    // Handles Stripe checkout functionality
    function onSubmit(data:CustomerForm){
        setPreventSubmit(true)
        
        // initialize Stripe instance
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/stripe/config/`, {
            headers: {
            'X-CSRFToken': csrfToken,
            'Content-Type': 'application/json',
            },
            withCredentials: true,
        })
        .then((res) => {
            if (res.status === 200) {
                // Create checkoutData with the cart and customer details
                const dataToSend: Checkout = {
                    order: Array.from(getCart()),
                    customerDetails: data
                }
                // initialize Stripe object
                const stripePromise = loadStripe(res.data.publicKey)

                // Send request to create checkout session
                axios.post(`${process.env.REACT_APP_SKILLJA_URL}/stripe/create_stripe_merchandise_checkout/`, dataToSend, {
                    headers: {
                    'X-CSRFToken': csrfToken,
                    'Content-Type': 'application/json',
                    },
                    withCredentials: true,
                }
                )
                .then(async (res) => {
                    if (res.status === 200) {
                        const stripe = await stripePromise;
                        if (stripe) {
                            // Proceed only if Stripe object is successfully initialized
                            stripe.redirectToCheckout({ sessionId: res.data.checkout_session_id })
                            .then((result) => {
                                if (result.error) {
                                    console.error("Stripe checkout error:", result.error.message)
                                }
                                else {
                                    // Redirect user to success or cancelled page, deemed by stripe
                                    window.location.href = res.data.checkout_session_url
                                }
                            })
                        } else {
                            console.error("Stripe.js failed to load")
                        }
                    }
                })
                .catch(handleError)
            } 
            else {console.error('Failed to get Public Stripe Key')}
        })
        .catch(handleError) 
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="mb-8 flex flex-col justify-center">
            <div className="grid grid-cols-2 gap-4">
                <div className="my-2 mr font-kulim">
                    <label className="block text-sm font-medium">First Name</label>
                    <input
                        {...register("firstName", { required: "First name is required" })}
                        className="w-full border p-2 rounded-lg"
                    />
                    {errors.firstName && <p className="text-red-500 text-sm">{String(errors.firstName.message)}</p>}
                </div>
                <div className="my-2 font-kulim">
                    <label className="block text-sm font-medium">Last Name</label>
                    <input
                        {...register("lastName", { required: "Last name is required" })}
                        className="w-full border p-2 rounded-lg"
                    />
                    {errors.lastName && <p className="text-red-500 text-sm">{String(errors.lastName.message)}</p>}
                </div>
                <div className="my-2 font-kulim">
                    <label className="block text-sm font-medium">Email</label>
                    <input
                    type="email"
                    {...register("email", { required: "Email is required", pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email" } })}
                    className="w-full border p-2 rounded-lg"
                    />
                    {errors.email && <p className="text-red-500 text-sm">{String(errors.email.message)}</p>}
                </div>

                <div className="my-2 font-kulim">
                    <label className="block text-sm font-medium">Phone Number</label>
                    <input
                    type="tel"
                    {...register("phoneNumber", { required: "Phone number is required", pattern: { value: /^[0-9]{10}$/, message: "Invalid phone number" } })}
                    className="w-full border p-2 rounded-lg"
                    />
                    {errors.phoneNumber && <p className="text-red-500 text-sm">{String(errors.phoneNumber.message)}</p>}
                </div>
                <div className="my-2 font-kulim">
                    <label className="block text-sm font-medium mb-2">Address</label>
                    <input
                    {...register("address", { required: "Address is required" })}
                    className="w-full border p-2 rounded-lg"
                    />
                    {errors.address && <p className="text-red-500 text-sm">{String(errors.address.message)}</p>}
                </div>
                <div className="my-2 font-kulim">
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input
                    {...register("city", { required: "City is required" })}
                    className="w-full border p-2 rounded-lg"
                    />
                    {errors.city && <p className="text-red-500 text-sm">{String(errors.city.message)}</p>}
                </div>
                <div className="my-2 font-kulim">
                    <label className="block text-sm font-medium mb-2">Province</label>
                    <select {...register("province", { required: "Province is required" })} className="w-full text-main-grey-200 border p-2.5 rounded-lg">
                    <option value="">Select Province</option>
                    <option value="ON">Ontario</option>
                    <option value="QC">Quebec</option>
                    <option value="BC">British Columbia</option>
                    <option value="AB">Alberta</option>
                    <option value="MB">Manitoba</option>
                    <option value="SK">Saskatchewan</option>
                    <option value="NB">New Brunswick</option>
                    <option value="NS">Nova Scotia</option>
                    <option value="PE">Prince Edward Island</option>
                    <option value="NL">Newfoundland and Labrador</option>
                    </select>
                    {errors.province && <p className="text-red-500 text-sm">{String(errors.province.message)}</p>}
                </div>
                <div className="my-2 font-kulim">
                    <label className="block text-sm font-medium mb-2">Postal Code</label>
                    <input
                        {...register("postalCode", { required: "Postal code is required", pattern: { value: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/, message: "Invalid postal code" } })}
                        className="w-full border p-2 rounded-lg"
                    />
                    {errors.postalCode && <p className="text-red-500 text-sm">{String(errors.postalCode.message)}</p>}
                </div>
            </div>
            <button 
                className="form-btn px-5 mx-auto mt-16"
                aria-label="submit merchandise order"
                tabIndex={0}
                type="submit"
                disabled={preventSubmit}
            >
                Checkout
            </button>
        </form>
    )
}